// What the collection page shows: your records, narrowed by search and filters, then sorted.
// State lives at module level so filters survive a trip to another page and back.
import { ref, computed, watch } from 'vue';
import { needsTagging } from '@/composables/useTagging';

export const SORTS = [
  { key: 'added', label: 'Recently added' },
  { key: 'title', label: 'Title A–Z' },
  { key: 'artist', label: 'Artist A–Z' },
  { key: 'yearDesc', label: 'Year, newest' },
  { key: 'yearAsc', label: 'Year, oldest' },
];

// Facet groups: within a group any selected value matches (OR); across groups all must match (AND)
const GROUPS = [
  { key: 'genres', label: 'Genre', values: r => r.genres || [] },
  { key: 'moods', label: 'Mood', values: r => r.moods || [] },
  { key: 'decades', label: 'Decade', values: r => (r.decade ? [`${r.decade}s`] : []) },
];

const selected = ref({ genres: [], moods: [], decades: [] });
const needsTags = ref(false);
const sort = ref('added');

// q is already lowercased; an empty search matches everything without touching the record
const matchesText = (record, q) => !q
  || [record.title, ...(record.artists || []), ...(record.labels || []), ...(record.formats || [])]
    .some(value => value && value.toLowerCase().includes(q));

const matchesGroup = (record, group) => {
  const picks = selected.value[group.key];
  return !picks.length || group.values(record).some(v => picks.includes(v));
};

const matchesNeedsTags = record => !needsTags.value || needsTagging(record);

// Sorting ignores a leading "The ", and keeps records without a year at the end either way
const sortKey = value => (value || '').replace(/^the\s+/i, '');
const byText = (a, b) => sortKey(a).localeCompare(sortKey(b), undefined, { sensitivity: 'base' });
const yearOf = r => r.originalYear || r.pressingYear || null;
const byYear = direction => (a, b) => {
  const [ya, yb] = [yearOf(a), yearOf(b)];
  if (!ya || !yb) return (ya ? 0 : 1) - (yb ? 0 : 1);
  return direction * (ya - yb);
};
const added = r => Date.parse(r.dateAdded) || 0;

const COMPARATORS = {
  added: (a, b) => added(b) - added(a),
  title: (a, b) => byText(a.title, b.title),
  artist: (a, b) => byText(a.artists[0], b.artists[0]) || byText(a.title, b.title),
  yearDesc: byYear(-1),
  yearAsc: byYear(1),
};

/**
 * @param {Ref<Array>} releases - the tagged collection
 * @param {Ref<string>} query - free-text search within the collection
 */
export default function useCollectionView(releases, query) {
  const matchesAll = (record, q, exceptGroup = null) => matchesText(record, q)
    && matchesNeedsTags(record)
    && GROUPS.filter(group => group.key !== exceptGroup).every(group => matchesGroup(record, group));

  const lowerQuery = computed(() => (query.value || '').toLowerCase());

  const visible = computed(() => releases.value
    .filter(r => matchesAll(r, lowerQuery.value))
    .sort(COMPARATORS[sort.value]));

  // Menu options per group. Counts reflect the other filters, so a choice never leads to an empty result
  // by surprise; ordering is by collection-wide size so entries don't jump around while filtering.
  const facets = computed(() => GROUPS.map(group => {
    const overall = {};
    const counts = {};
    releases.value.forEach(r => group.values(r).forEach(v => { overall[v] = (overall[v] || 0) + 1; }));
    releases.value.filter(r => matchesAll(r, lowerQuery.value, group.key))
      .forEach(r => group.values(r).forEach(v => { counts[v] = (counts[v] || 0) + 1; }));

    const values = Object.keys(overall);
    values.sort(group.key === 'decades' ? (a, b) => b.localeCompare(a) : (a, b) => overall[b] - overall[a]);
    return {
      key: group.key,
      label: group.label,
      selectedCount: selected.value[group.key].length,
      options: values.map(value => ({
        value, count: counts[value] || 0, active: selected.value[group.key].includes(value),
      })),
    };
  }));

  const toggle = (key, value) => {
    const picks = selected.value[key];
    selected.value = {
      ...selected.value,
      [key]: picks.includes(value) ? picks.filter(v => v !== value) : [...picks, value],
    };
  };

  const clearGroup = key => { selected.value = { ...selected.value, [key]: [] }; };

  const clearFilters = () => {
    selected.value = { genres: [], moods: [], decades: [] };
    needsTags.value = false;
  };

  const needsTagsCount = computed(() => releases.value.filter(needsTagging).length);

  // Drop selections that no record has any more (e.g. after retagging), so a filter can't stay
  // active while its option has disappeared from the menu
  watch(releases, list => {
    if (!list.length) return;
    const pruned = {};
    GROUPS.forEach(group => {
      const present = new Set(list.flatMap(group.values));
      pruned[group.key] = selected.value[group.key].filter(v => present.has(v));
    });
    if (GROUPS.some(group => pruned[group.key].length !== selected.value[group.key].length)) {
      selected.value = pruned;
    }
  });

  const activeFilterCount = computed(() => Object.values(selected.value)
    .reduce((n, picks) => n + picks.length, needsTags.value ? 1 : 0));

  return {
    visible, facets, sort, needsTags, needsTagsCount, activeFilterCount, toggle, clearGroup, clearFilters,
  };
}
